import React from 'react';
import styled, { css } from 'styled-components';
import { CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

const variants = {
  success: css`
    background: var(--success-soft);
    border-color: #c5e0c9;
    color: var(--success);
  `,
  error: css`
    background: var(--error-soft);
    border-color: #f2c7c3;
    color: var(--error);
  `,
  warning: css`
    background: var(--warning-soft);
    border-color: #ecd3ae;
    color: var(--warning);
  `,
  info: css`
    background: var(--info-soft);
    border-color: #c3d7e8;
    color: var(--info);
  `,
};

const Box = styled.div<{ $variant: AlertVariant }>`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-md);
  border: 1px solid;
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  margin-bottom: var(--space-md);

  ${({ $variant }: { $variant: AlertVariant }) => variants[$variant]}

  svg {
    width: 1.125rem;
    height: 1.125rem;
    flex-shrink: 0;
    margin-top: 0.0625rem;
  }
`;

const icons: Record<AlertVariant, React.ReactNode> = {
  success: <CheckCircle2 />,
  error: <AlertCircle />,
  warning: <AlertTriangle />,
  info: <Info />,
};

interface AlertProps {
  $variant?: AlertVariant;
  children: React.ReactNode;
  className?: string;
}

/** Banner di feedback inline: <Alert $variant="error">Messaggio</Alert> */
export const Alert: React.FC<AlertProps> = ({ $variant = 'info', children, className }) => (
  <Box $variant={$variant} className={className} role={$variant === 'error' ? 'alert' : 'status'}>
    {icons[$variant]}
    <div>{children}</div>
  </Box>
);

export default Alert;
